import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';
import { resolveDealerHostedStoreContext } from '../../util/urlHelpers';
import ItemSourceToggle from '../ItemSourceToggle/ItemSourceToggle';
import KeywordAutocompleteInput from '../KeywordAutocompleteInput/KeywordAutocompleteInput';

import css from './TopbarIframe.module.css';

/**
 * Componente Topbar diseñado exclusivamente para ser renderizado
 * cuando la aplicación está incrustada dentro de un iframe.
 * Incluye la estructura compartida del shell iframe: buscador opcional,
 * controles mobile opt-in y navegación superior de categorías para excepciones futuras.
 */
const TopbarIframe = props => {
  const {
    className,
    rootClassName,
    // Props opcionales inyectados desde StorePage para mantener su estado
    iframeCategories = [],
    iframeCurrentCategory,
    onIframeCategoryClick,
    iframeSearchKeyword,
    iframeSearchPath = '/s',
    iframeSearchPlaceholder = "Tell us more about what you're looking for...",
    iframeSearchTitle = 'Search Products',
    setIframeSearchKeyword,
    onIframeSearchSubmit,
    iframeCatalogLabel = 'All Products',
    setMobileMenuOpen,
    showDesktopCategoryTabs = false,
    showMobileCategoryMenuButton = false,
    showMobileItemSourceToggle = false,
    mobileItemSourceValue = 'in-store',
    onMobileItemSourceChange,
    hideMobileSearchBanner = false,
    // opt-in `/s` autocomplete: { fetchSuggestions, onSelect } (KeywordAutocompleteInput)
    iframeSearchSuggestions = null,
  } = props;

  const history = useHistory();
  const config = useConfiguration();

  // Fallback a categorías globales del marketplace si la página actual no las provee (ej. CartPage, SearchPage)
  let displayCategories = iframeCategories;
  if (!displayCategories || displayCategories.length === 0) {
    const listingFields = config?.listing?.listingFields || [];
    const categoryField = listingFields.find(lf => lf.key === 'category');
    if (categoryField && categoryField.enumOptions) {
      displayCategories = categoryField.enumOptions;
    }
  }

  // Estado local de búsqueda (Fallback si no se provee desde la página padre)
  const [localSearchKeyword, setLocalSearchKeyword] = useState('');
  const keyword = iframeSearchKeyword !== undefined ? iframeSearchKeyword : localSearchKeyword;
  const setKeyword = setIframeSearchKeyword || setLocalSearchKeyword;
  const shouldRenderSearch =
    iframeSearchKeyword !== undefined ||
    typeof setIframeSearchKeyword === 'function' ||
    typeof onIframeSearchSubmit === 'function';

  const [mobileMenuOpen, setLocalMobileMenuOpen] = useState(false);
  const handleOpenMobileMenu = () => {
    if (setMobileMenuOpen) setMobileMenuOpen(true);
    setLocalMobileMenuOpen(true);
  };
  const handleCloseMobileMenu = () => {
    if (setMobileMenuOpen) setMobileMenuOpen(false);
    setLocalMobileMenuOpen(false);
  };

  const handleKeywordSearch = async e => {
    e?.preventDefault();
    if (onIframeSearchSubmit) {
      onIframeSearchSubmit(e);
    } else {
      const query = encodeURIComponent(keyword);
      const { dealerId } =
        typeof window !== 'undefined'
          ? await resolveDealerHostedStoreContext(window.location.hostname)
          : { dealerId: null };
      if (dealerId) {
        history.push(`/store/${dealerId}?keywords=${query}&iframe=true`);
      } else {
        history.push(`${iframeSearchPath}?keywords=${query}&iframe=true`);
      }
    }
  };

  const handleCategoryClick = async option => {
    if (onIframeCategoryClick) {
      onIframeCategoryClick(option);
    } else {
      // Resolve the dealer's store ID from the iframe hostname so category
      // searches stay scoped to this store instead of showing all listings.
      const { dealerId } =
        typeof window !== 'undefined'
          ? await resolveDealerHostedStoreContext(window.location.hostname)
          : { dealerId: null };
      if (dealerId) {
        history.push(
          `/store/${dealerId}?iframe=true${option !== 'all' ? `&pub_category=${option}` : ''}`
        );
      } else if (option === 'all') {
        history.push(`${iframeSearchPath}?iframe=true`);
      } else {
        history.push(`${iframeSearchPath}?pub_category=${option}&iframe=true`);
      }
    }
  };

  const classes = classNames(rootClassName || css.root, className);

  const shouldRenderMobileTopHeader = showMobileItemSourceToggle || showMobileCategoryMenuButton;

  return (
    <div className={classes}>
      {/* ===== Optional Category Tabs + Cart (Desktop) ===== */}
      {showDesktopCategoryTabs ? (
        <div className={`${css.categoriesSelector} ${css.categoriesSelectorIframe}`}>
          <div className={css.categoriesList}>
            {displayCategories.map(cat => (
              <div
                key={cat.option}
                onClick={() => handleCategoryClick(cat.option)}
                className={
                  iframeCurrentCategory === cat.option ? css.categoryTabSelected : css.categoryTab
                }
              >
                {cat.label}
              </div>
            ))}
            <div
              onClick={() => handleCategoryClick('all')}
              className={
                iframeCurrentCategory === 'all' || !iframeCurrentCategory
                  ? css.categoryTabSelected
                  : css.categoryTab
              }
            >
              {iframeCatalogLabel}
            </div>
          </div>
          <div className={css.cartDesktopContainer}>
            {/* <CartIconBadge count={cartItemCount} className={css.cartIconDesktop} /> */}
          </div>
        </div>
      ) : null}

      {/* ===== Mobile-only header: render only when a mobile control is intentionally enabled ===== */}
      {shouldRenderMobileTopHeader ? (
        <div
          className={classNames(css.mobileTopHeader, {
            [css.mobileTopHeaderWithToggle]: showMobileItemSourceToggle,
          })}
        >
          {showMobileItemSourceToggle ? (
            <ItemSourceToggle
              className={css.mobileTopbarToggle}
              fullBleedMobile
              value={mobileItemSourceValue}
              onChange={onMobileItemSourceChange}
              hideLabel
              label="Item Source"
            />
          ) : null}
          {showMobileCategoryMenuButton ? (
            <button className={css.hamburgerButton} onClick={handleOpenMobileMenu}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#1E1E1E"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>
          ) : null}
        </div>
      ) : null}

      {shouldRenderSearch ? (
        <div
          className={classNames(css.iframeBannerContainerVisible, {
            [css.iframeBannerHiddenMobile]: hideMobileSearchBanner,
          })}
        >
          <h1 className={css.bannerTitle}>{iframeSearchTitle}</h1>
          <form className={css.bannerInputRow} onSubmit={handleKeywordSearch}>
            {iframeSearchSuggestions ? (
              <KeywordAutocompleteInput
                id="iframe-keyword-search"
                className={css.bannerSearchInputWrap}
                inputClassName={css.bannerSearchInput}
                value={keyword}
                onChange={setKeyword}
                onSelect={iframeSearchSuggestions.onSelect}
                fetchSuggestions={iframeSearchSuggestions.fetchSuggestions}
                placeholder={iframeSearchPlaceholder}
                ariaLabel={iframeSearchTitle}
              />
            ) : (
              <input
                className={css.bannerSearchInput}
                type="text"
                placeholder={iframeSearchPlaceholder}
                value={keyword}
                onChange={e => setKeyword(e.target.value)}
              />
            )}
            <button className={css.bannerSearchButton} type="submit">
              Go
            </button>
          </form>
        </div>
      ) : null}

      {/* ===== Mobile Category Menu (Fullscreen Modal) ===== */}
      {mobileMenuOpen && (
        <div className={css.mobileMenu}>
          <div className={css.mobileMenuHeader}>
            <button className={css.mobileMenuBack} onClick={handleCloseMobileMenu}>
              <svg
                width="5"
                height="9"
                viewBox="0 0 5 9"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1.05 4.45L4.95 8.4L4.45 8.9L0 4.45L4.45 0L4.95 0.5L1.05 4.4V4.45Z"
                  fill="#1E1E1E"
                />
              </svg>
              Back
            </button>
            <button className={css.mobileMenuClose} onClick={handleCloseMobileMenu}>
              Close
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                stroke="#1E1E1E"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <line x1="1" y1="1" x2="13" y2="13" />
                <line x1="13" y1="1" x2="1" y2="13" />
              </svg>
            </button>
          </div>

          <div className={css.mobileMenuList}>
            <div
              className={css.mobileMenuItem}
              onClick={() => {
                handleCategoryClick('all');
                handleCloseMobileMenu();
              }}
            >
              <span className={css.mobileMenuLabel}>{iframeCatalogLabel}</span>
            </div>
            {displayCategories.map(cat => (
              <div
                key={cat.option}
                className={css.mobileMenuItem}
                onClick={() => {
                  handleCategoryClick(cat.option);
                  handleCloseMobileMenu();
                }}
              >
                <span className={css.mobileMenuLabel}>{cat.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

TopbarIframe.defaultProps = {
  className: null,
  rootClassName: null,
  showDesktopCategoryTabs: false,
};

TopbarIframe.propTypes = {
  className: PropTypes.string,
  rootClassName: PropTypes.string,
  iframeSearchPath: PropTypes.string,
  iframeSearchPlaceholder: PropTypes.string,
  iframeSearchTitle: PropTypes.string,
  iframeCatalogLabel: PropTypes.string,
  showDesktopCategoryTabs: PropTypes.bool,
  showMobileCategoryMenuButton: PropTypes.bool,
  showMobileItemSourceToggle: PropTypes.bool,
  mobileItemSourceValue: PropTypes.string,
  onMobileItemSourceChange: PropTypes.func,
  hideMobileSearchBanner: PropTypes.bool,
  iframeSearchSuggestions: PropTypes.shape({
    fetchSuggestions: PropTypes.func.isRequired,
    onSelect: PropTypes.func.isRequired,
  }),
};

export default TopbarIframe;
